<script lang="ts" setup>
import AuthDebug from './auth-debug.vue';
import EnvironmentDebug from './environment-debug.vue';
import EnvironmentVariablesDebug from './environment-variables-debug.vue';
import LocaleDebug from './locale-debug.vue';
import RegionDebug from './region-debug.vue';
import FeatureFlagDebug from './feature-flag-debug.vue';
import ModalDebug from './modal-debug.vue';
import ToastDebug from './toast-debug.vue';
import QueryDebug from './query-debug.vue';
import ApiCacheDebug from './api-cache-debug.vue';
import Button from '../design-system/button.vue';
import { CogIcon } from '@heroicons/vue/24/outline';
import Drawer from '../design-system/drawer.vue';
import HamburgerMenuHeader from '~/components/user-interface/hamburger/hamburger-menu-header.vue';
import { useEnvironment } from '~/composables/useStates/useEnvironment';
import { useMagicKeys } from '@vueuse/core';
import type { FeatureFlag } from '~/interfaces/dto/config/response';
import { deepClone } from '@vue/devtools-shared';
import { globalKeys } from 'assets/data/global-keys';
import ErrorBoundary from '~/components/user-interface/error-boundary.vue';
import GameDebug from '~/components/tools/game-debug.vue';
import RouteDebug from './route-debug.vue';
import WebSocketDebug from './web-socket-debug.vue';

const environment = useEnvironment();
const colorMode = useColorMode();
const authStore = useAuthStore();
const siteStore = useSiteStore();
const gameStore = useGameStore();

const route = useRoute();

const open = ref<boolean>(false);
const activeFeatureFlags = ref<boolean>(false);
const featureFlags = ref<FeatureFlag[]>(
  deepClone(siteStore.getConfig?.featureFlags),
);

const { $enabled, $apiCallCache } = useNuxtApp();
const { $storageService } = useServices();
const { Ctrl_shift_s, Ctrl_t } = useMagicKeys();

const isDev = computed(
  () => import.meta.dev || environment.value.environment.testHost,
);

const clearCache = () => {
  $apiCallCache.value = new Map();
  console.log(
    `%c ************** clearing the api call cache **************`,
    'background: #222; color: #bada55',
  );
  console.table(Object.fromEntries($apiCallCache.value));
};

const availableTools = computed(() => {
  const tools = [
    {
      title: 'Account / Auth',
      enabled: authStore.isLoggedIn,
      component: AuthDebug,
    },
    {
      title: 'Locale',
      enabled: true,
      component: LocaleDebug,
    },
    {
      title: 'Region',
      enabled: isDev.value,
      component: RegionDebug,
    },
    {
      title: 'Environment',
      enabled: isDev.value,
      component: EnvironmentDebug,
    },
    {
      title: 'Environment Variables',
      enabled: isDev.value,
      component: EnvironmentVariablesDebug,
    },
    {
      title: 'Feature Flags',
      enabled: true,
      component: FeatureFlagDebug,
    },
    {
      title: 'Modals',
      enabled: true,
      component: ModalDebug,
    },
    {
      title: 'Toast Debug',
      enabled: true,
      component: ToastDebug,
    },
    {
      title: 'Query String',
      enabled: true,
      component: QueryDebug,
    },
    {
      title: 'Current game Debug',
      enabled: !!gameStore.currentGame && !!route.params?.game,
      component: GameDebug,
    },
    {
      title: 'Force Route',
      enabled: true,
      component: RouteDebug,
    },
    {
      title: 'Web Socket Debug',
      enabled: true,
      component: WebSocketDebug,
    },
    {
      title: 'Debug Disable Cache',
      enabled: true,
      component: ApiCacheDebug,
    },
  ];
  return tools.filter(({ enabled }) => enabled);
});

const featureFlagIdentifier = computed(
  () =>
    ({ group, setting }) =>
      `${group}.${setting}`.toLowerCase(),
);

const toggleFeatureFlag = ({
  group,
  setting,
  val,
  flagIdentifier,
}: {
  flagIdentifier?: string;
  group?: string;
  setting?: string;
  val?: boolean;
  rehydrating?: boolean;
}) => {
  const identifier =
    flagIdentifier || featureFlagIdentifier.value({ group, setting });
  const value = typeof val !== 'undefined' ? val : !$enabled(identifier);
  siteStore.featureFlags.set(identifier, value);
  activeFeatureFlags.value = true;
  $storageService.setSessionStorage({
    key: globalKeys.developerConfigKeys.debugFeatureFlagState,
    data: Array.from(siteStore.featureFlags.entries()),
  });
};

const rehydrateFeatureFlags = () => {
  const persistedState = $storageService.getSessionStorage<[string, boolean][]>(
    {
      key: globalKeys.developerConfigKeys.debugFeatureFlagState,
    },
  );

  if (!!persistedState) {
    activeFeatureFlags.value = true;
    persistedState?.forEach(([key, val]) =>
      toggleFeatureFlag({ flagIdentifier: key, val, rehydrating: true }),
    );
  }
};
const resetFeatureFlags = () => {
  activeFeatureFlags.value = false;
  $storageService.removeSessionStorage({
    key: globalKeys.developerConfigKeys.debugFeatureFlagState,
  });
  siteStore.setConfig({
    ...siteStore.getConfig,
    featureFlags: featureFlags.value,
  });
};

onMounted(() => rehydrateFeatureFlags());

watchEffect(() => {
  if (Ctrl_shift_s.value) open.value = !open.value;
  if (Ctrl_t.value) {
    if (colorMode.preference === 'dark') colorMode.preference = 'light';
    else colorMode.preference = 'dark';
  }
});

const hasApiDebugEnabled = computed(() =>
  $storageService.getSessionStorage({
    key: globalKeys.developerConfigKeys.debugDisableCache,
  }),
);

provide('clearApiCache', clearCache);
</script>
<template>
  <div
    class="fixed bottom-32 md:bottom-4 right-2 md:right-4"
    :style="{ zIndex: 1103 }"
  >
    <Drawer v-model:visible="open" position="right">
      <template #activator>
        <div
          v-if="activeFeatureFlags && !open"
          class="text-xs bg-success-500 text-light-50 rounded text-center p-1 my-2 font-bold"
        >
          Flags Adjusted
        </div>
        <div
          v-if="!open"
          class="text-xs bg-primary-blue-500 text-light-50 rounded text-center p-1 my-2 font-bold uppercase"
        >
          {{ environment.regionConfigs.runtimeEnvironment }}
        </div>
        <DesignSystemButton
          v-if="$apiCallCache.size > 0 && !open && hasApiDebugEnabled"
          class="text-xs mb-2"
          @click="clearCache"
        >
          Clear Api Log
        </DesignSystemButton>
        <div
          v-if="!open"
          class="p-2 bg-stacked flex gap-2 justify-between stacked-border rounded-xl"
        >
          <div class="text-xs font-bold text-center text-base-priority">
            <div>Debug tools</div>
            <div>
              {{ isDev ? 'Local' : 'Deployed' }}
            </div>
            <Button type="tertiary" class="mx-auto" @click="open = !open">
              <CogIcon :key="`${open}-cog`" class="hover:animate-spin" />
            </Button>
          </div>
        </div>
      </template>
      <template #header>
        <div class="w-full">
          <div
            class="text-sm font-bold text-center bg-primary-blue-500 text-light-50 p-2 capitalize w-full"
          >
            {{ environment.regionConfigs.runtimeEnvironment }}
          </div>
          <HamburgerMenuHeader @toggle-menu="open = !open" />
        </div>
      </template>
      <div class="flex">
        <div class="grid grid-cols-1 gap-2 p-4 bg-layer-secondary">
          <div
            v-for="(tool, index) in availableTools"
            :key="index"
            :class="{
              'border-t stacked-border-color': index > 0,
            }"
          >
            <ErrorBoundary
              component-boundary="Debug tools"
              file="debug-toolbar.vue"
            >
              <div class="text-sm text-base-priority my-2 font-bold">
                {{ tool.title }}:
              </div>
              <component
                :is="tool.component"
                :key="tool.title"
                @toggle-feature-flag="toggleFeatureFlag"
                @reset-feature-flags="resetFeatureFlags()"
                @close-dev-tools="open = false"
              />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>
